import { Reveal, SectionHead } from "./Reveal";
import { ROLE_ITEMS, GOALS } from "@/data/content";
import { CheckCircle2 } from "lucide-react";

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1594233666755-d1cb282abd25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmRpYSUyMGNvcnBvcmF0ZSUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzkwNDg2NTYwfDA&ixlib=rb-4.1.0&q=85";

const About = () => (
    <section id="about" className="py-24 sm:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
                <div>
                    <SectionHead
                        eyebrow="About Us"
                        title="About Pramukh Compliance Services"
                        sub="We provide professional labour-law and statutory compliance support to businesses across Gujarat and other locations in India — companies, branches, contractors and employers."
                    />
                    <Reveal delay={0.1}>
                        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="about-focus-list">
                            {[
                                "Statutory compliance management",
                                "Labour law documentation",
                                "Registration & licensing support",
                                "Government department coordination",
                                "Compliance due-date monitoring",
                                "Statutory register maintenance",
                                "Branch-level compliance",
                                "Compliance risk identification",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                                    <CheckCircle2 size={16} className="text-sky-400 mt-0.5 shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <blockquote className="mt-10 border-l-2 border-sky-400 pl-6 text-base sm:text-lg text-slate-200 leading-relaxed italic" data-testid="about-approach-quote">
                            "Our approach is simple — understand the business requirement, identify the applicable
                            compliance, complete the required documentation and registration, monitor statutory due
                            dates, and help the organisation maintain proper records."
                        </blockquote>
                    </Reveal>
                </div>
                <Reveal delay={0.12}>
                    <div className="relative">
                        <div className="absolute -inset-3 border border-sky-400/20 pointer-events-none" />
                        <img src={ABOUT_IMG} alt="Modern corporate office in India" className="w-full aspect-[4/5] object-cover" />
                        <div className="absolute bottom-5 left-5 right-5 bg-[#0A1128]/90 backdrop-blur border border-white/10 p-5">
                            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-sky-400">Brand Message</p>
                            <p className="mt-2 font-heading text-sm sm:text-base font-semibold text-white leading-snug">
                                Pramukh Compliance Services — Simplifying Labour Law & Statutory Compliance for Businesses.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>

            <div className="mt-28">
                <SectionHead
                    eyebrow="Our Role"
                    title="Our Role in Your Compliance Management"
                    sub="We act as your compliance support partner and help your organisation manage its statutory obligations end to end."
                />
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="role-grid">
                    {ROLE_ITEMS.map((r, i) => (
                        <Reveal key={r.title} delay={(i % 4) * 0.07}>
                            <div className="h-full border border-white/10 bg-[#101D3F]/50 p-6 hover:border-sky-400/40 transition-colors duration-300">
                                <span className="font-mono2 text-[10px] text-sky-500 tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-3 font-heading text-base font-semibold text-white">{r.title}</h3>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{r.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>

            <div className="mt-28 border border-white/10 bg-gradient-to-br from-[#101D3F] to-[#0A1128] p-8 sm:p-14" data-testid="goal-section">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <SectionHead
                        eyebrow="Our Goal"
                        title="To make statutory compliance simple, organised and manageable for every business."
                    />
                    <div className="flex flex-wrap gap-3">
                        {GOALS.map((g, i) => (
                            <Reveal key={g} delay={i * 0.05}>
                                <span className="inline-block px-4 py-2 rounded-full border border-sky-400/25 bg-sky-400/5 text-sm text-slate-200">
                                    {g}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

export default About;
